import React from "react";
import { Link } from "react-router-dom";
import { MessageDescriptor, useIntl } from "react-intl";
import { msgs } from "../msgs";
import { WidgetEntity } from "../types";

/**
 * Table presets keep <WidgetTable> generic. The widget itself knows nothing about
 * promotions (or any other domain): it just renders rows. Domain-specific behaviour —
 * which columns to show, in what order, their localized headers and any custom cell
 * rendering (links, badges…) — lives in a preset here.
 *
 * A preset is selected primarily by the widget's `entity` (sent by the backend), which
 * is deterministic. A `match` predicate over the row keys is kept as a fallback for
 * payloads with no entity (e.g. messages persisted before `entity` existed). To support
 * a new kind of tabular data, add a TablePreset to TABLE_PRESETS — set its `entity` and,
 * ideally, a `match` fallback. No changes to <WidgetTable> are needed. Tables with no
 * matching preset fall back to a generic render (one column per key, raw header).
 */

export interface TableCellContext {
  /** The full row object (so a renderer can read sibling fields like `id`). */
  row: Record<string, unknown>;
  /** Engine ticker from the surrounding widget — a fallback for building links. */
  propertyTicker?: string;
}

export interface TableColumn {
  /** The row property this column reads. */
  key: string;
  /** Localized header label; falls back to the raw key when omitted. */
  labelMsg?: MessageDescriptor;
  /**
   * Custom cell renderer. Receives the already-formatted display string (booleans become
   * ✓/✗) plus the row context. Return a string to keep the default rendering.
   */
  renderCell?: (display: string, ctx: TableCellContext) => React.ReactNode;
}

export interface TablePreset {
  /** Identifier — used as a React key and for debugging. */
  id: string;
  /**
   * The widget entity this preset renders. Primary, deterministic selector: when the
   * widget carries an `entity`, the matching preset is used regardless of row keys.
   */
  entity: WidgetEntity;
  /**
   * Fallback selector for payloads with no `entity` (e.g. legacy persisted messages):
   * true when this preset should render rows with the given keys. First match wins.
   */
  match?: (keys: string[]) => boolean;
  /** Explicit, ordered columns. Any key not listed here is intentionally hidden. */
  columns: TableColumn[];
}

// Turns the ticker cell into a link to the promotion. Prefers the row's own
// propertyTicker (child engine, when listing from an aggregator), falling back to the
// engine ticker passed to the widget. Renders plain text when either piece is missing.
function promotionTickerLink(
  display: string,
  { row, propertyTicker }: TableCellContext
): React.ReactNode {
  const rowTicker =
    (typeof row.propertyTicker === "string" && row.propertyTicker) || propertyTicker;
  const id = typeof row.id === "string" ? row.id : undefined;
  if (!rowTicker || !id) return display;
  return (
    <Link
      to={`/${rowTicker}/promotions/${id}`}
      className="inline-flex items-center gap-1 text-[#4F46E5] hover:underline">
      {display}
      <em className="fa fa-external-link text-[10px]" />
    </Link>
  );
}

// Renders the sharing type. Only SHARED is shown (localized); SIMPLE (and anything else)
// renders empty, since we don't want to surface it in the listing.
function SharingTypeCell({ value }: { value: string }): React.ReactNode {
  const intl = useIntl();
  if (value === "SHARED") return intl.formatMessage(msgs.fieldSharingTypeShared);
  return "";
}

// Renders the rate type, localizing the backend enum value ("PERCENTAGE" | "CONSTANT").
// Unknown values fall back to the raw display string.
function RateTypeCell({ value }: { value: string }): React.ReactNode {
  const intl = useIntl();
  if (value === "PERCENTAGE") return intl.formatMessage(msgs.rateTypePercentage);
  if (value === "CONSTANT") return intl.formatMessage(msgs.rateTypeConstant);
  return value;
}

// The backend sends the group as "GROUP_<n>"; we only want to display the number.
function promotionGroupCell(display: string): React.ReactNode {
  return display.replace(/^GROUP_/, "");
}

export const PROMOTION_TABLE_PRESET: TablePreset = {
  id: "promotions",
  entity: "promotion",
  // A promotion row carries both a base58 `id` and a human `ticker`.
  match: keys => keys.includes("id") && keys.includes("ticker"),
  columns: [
    { key: "isActive", labelMsg: msgs.fieldIsActive },
    { key: "isVisible", labelMsg: msgs.fieldIsVisible },
    { key: "ticker", labelMsg: msgs.fieldTicker, renderCell: promotionTickerLink },
    { key: "name", labelMsg: msgs.fieldName },
    { key: "rate", labelMsg: msgs.fieldRate },
    {
      key: "rateType",
      labelMsg: msgs.fieldRateType,
      renderCell: display => <RateTypeCell value={display} />,
    },
    {
      key: "sharingType",
      labelMsg: msgs.fieldSharingType,
      renderCell: display => <SharingTypeCell value={display} />,
    },
    { key: "group", labelMsg: msgs.fieldGroup, renderCell: promotionGroupCell },
  ],
};

export const LANGUAGE_TABLE_PRESET: TablePreset = {
  id: "languages",
  entity: "language",
  // A language row carries a `code` plus the `primary` flag (no `id`/`ticker`).
  match: keys => keys.includes("code") && keys.includes("primary"),
  columns: [
    { key: "name", labelMsg: msgs.fieldName },
    { key: "code", labelMsg: msgs.fieldCode },
    { key: "primary", labelMsg: msgs.fieldPrimary },
    { key: "active", labelMsg: msgs.fieldIsActive },
    { key: "visible", labelMsg: msgs.fieldIsVisible },
  ],
};

export const INVENTORY_TABLE_PRESET: TablePreset = {
  id: "inventory",
  entity: "inventory",
  // An inventory line carries accommodation + mealPlan components.
  match: keys => keys.includes("accommodation") && keys.includes("mealPlan"),
  // Show the human-readable component names (the short tickers are opaque codes).
  columns: [
    { key: "ticker", labelMsg: msgs.fieldTicker },
    { key: "accommodationName", labelMsg: msgs.fieldAccommodation },
    { key: "configurationName", labelMsg: msgs.fieldConfiguration },
    { key: "mealPlanName", labelMsg: msgs.fieldMealPlan },
    { key: "conditionName", labelMsg: msgs.fieldCondition },
  ],
};

export const TABLE_PRESETS: TablePreset[] = [
  PROMOTION_TABLE_PRESET,
  LANGUAGE_TABLE_PRESET,
  INVENTORY_TABLE_PRESET,
];

/**
 * Selects the table preset for a widget. Prefers the deterministic `entity` (sent by the
 * backend); when absent, falls back to matching the row keys. Returns undefined for a
 * generic table.
 */
export function resolveTablePreset(keys: string[], entity?: WidgetEntity): TablePreset | undefined {
  if (entity) {
    const byEntity = TABLE_PRESETS.find(preset => preset.entity === entity);
    if (byEntity) return byEntity;
  }
  return TABLE_PRESETS.find(preset => preset.match?.(keys));
}
